import { useState, useCallback } from 'react';
import { handleError } from '../utils/errorHandling/errorService';
import { ErrorCategory, ErrorSeverity } from '../utils/errorHandling/errorTypes';

/**
 * Custom hook for handling errors in functional components
 * 
 * This hook provides a way to handle errors in async operations,
 * log them properly, and manage error state in the component.
 * 
 * @param {Object} options - Configuration options
 * @returns {Object} Error handling utilities
 */
const useErrorHandler = (options = {}) => {
  const {
    initialError = null,
    category = ErrorCategory.UNKNOWN,
    severity = ErrorSeverity.ERROR,
    context = {},
    onError = null
  } = options;

  const [error, setError] = useState(initialError);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Clear the current error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Handle an error by logging it and updating state
   * 
   * @param {Error|string} err - The error to handle
   * @param {Object} errorOptions - Additional options for this specific error
   */
  const handleComponentError = useCallback((err, errorOptions = {}) => {
    if (!err) return;

    // Process the error through the central error service
    const userMessage = handleError(err, {
      category: errorOptions.category || category,
      severity: errorOptions.severity || severity,
      context: {
        ...context,
        ...errorOptions.context
      }
    });

    // Update component state with the sanitized error message
    setError(userMessage);

    // Call the optional onError callback if provided
    if (onError && typeof onError === 'function') {
      onError(userMessage, err);
    }
  }, [category, severity, context, onError]);

  /**
   * Wrap an async function with error handling
   * 
   * @param {Function} asyncFn - The async function to wrap
   * @param {Object} asyncOptions - Options for this specific async operation
   * @returns {Function} Wrapped function with error handling
   */
  const withErrorHandling = useCallback((asyncFn, asyncOptions = {}) => {
    return async (...args) => {
      try {
        setIsLoading(true);
        clearError();
        return await asyncFn(...args);
      } catch (err) {
        handleComponentError(err, asyncOptions);
        throw err; // Re-throw to allow caller to handle if needed
      } finally {
        setIsLoading(false);
      }
    };
  }, [handleComponentError, clearError]);

  /**
   * Execute an async function with error handling
   * 
   * @param {Function} asyncFn - The async function to execute
   * @param {Array|Object} args - Arguments to pass to the function, or options if no args needed
   * @param {Object} asyncOptions - Options for this specific async operation
   * @returns {Promise<any>} Result of the async function
   */
  const executeWithErrorHandling = useCallback(async (asyncFn, args, asyncOptions = {}) => {
    // Handle case where args is actually options (no args needed)
    if (args && !Array.isArray(args) && typeof args === 'object' && !asyncOptions.hasOwnProperty('category')) {
      asyncOptions = args;
      args = [];
    }

    // Ensure args is always an array
    const argsArray = Array.isArray(args) ? args : [];

    try {
      setIsLoading(true);
      clearError();
      return await asyncFn(...argsArray);
    } catch (err) {
      handleComponentError(err, asyncOptions);
      return null; // Return null instead of re-throwing
    } finally {
      setIsLoading(false);
    }
  }, [handleComponentError, clearError]);

  return {
    error,
    isLoading,
    clearError,
    handleError: handleComponentError,
    withErrorHandling,
    executeWithErrorHandling
  };
};

export default useErrorHandler;
